<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { useHead } from '@unhead/vue';
import ScrollReveal from '@/components/ui/animation/ScrollReveal.vue';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
} from '@app/presentation/components/site/constants';
import PaperLetter from '@app/presentation/components/paper/PaperLetter.vue';
import SeoCrumbs from '@app/presentation/components/seo/SeoCrumbs.vue';
import SeoFaqItem from '@app/presentation/components/seo/SeoFaqItem.vue';
import SeoFinalCta from '@app/presentation/components/seo/SeoFinalCta.vue';

// Informational guide for the `как задавать вопросы таро` cluster (how to
// FORMULATE a tarot question). Deliberately a top-level page (like
// /gadanie-onlayn), NOT under /rasklady — it must not cannibalize the
// transactional spread /rasklady/zadaj-vopros («Таро на Вопрос Онлайн»): this
// page teaches how to ask, the spread answers. Self-canonical + cross-linked.
const TITLE = 'Как задавать вопросы таро — гайд с примерами | Таро Мяу';
const DESCRIPTION =
  'Как задавать вопросы таро, чтобы получить честный ответ: как сформулировать вопрос, примеры для отношений, работы и решений — и чего избегать.';
const URL = 'https://tarotmeow.ru/kak-zadavat-voprosy-taro';

const principles = [
  {
    title: 'Спрашивай о себе, а не о других',
    text: 'Таро точнее всего читает твою часть истории — твои чувства, выбор, следующий шаг. «Что мне сделать?» работает глубже, чем «что он думает?».',
  },
  {
    title: 'Открытый вопрос глубже закрытого',
    text: '«Да / нет» сужает ответ до точки — хорош, когда нужен быстрый толчок на распутье. «Что», «как», «почему» дают карте пространство, а тебе — инсайт.',
  },
  {
    title: 'Одна тема за один раз',
    text: 'Не склеивай «уходить ли мне и встречу ли другого». Один расклад — один вопрос, иначе карты отвечают на всё сразу и ни на что конкретно.',
  },
  {
    title: 'Про настоящее и действие, а не про дату',
    text: 'Вместо «когда» спрашивай «что сейчас помогает, а что мешает». Таро показывает тенденцию и рычаги, а не число в календаре.',
  },
  {
    title: 'Конкретно, но не тесно',
    text: 'Одна карта — как луч фонарика: всю комнату не осветит, но покажет, куда смотреть. «Что меня ждёт?» даёт туман; ищи середину — «что мне важно понять про…».',
  },
];

// Each category cross-links to the spread that best fits it (internal-link equity).
const categories = [
  {
    label: 'Любовь и отношения',
    to: '/rasklady/lyubovnyj-rasklad',
    linkLabel: 'Любовный расклад',
    examples: [
      'Что он ко мне чувствует прямо сейчас?',
      'Стоит ли мне написать ему первой на этой неделе?',
      'Как пройдёт наша встреча с Сашей в субботу?',
      'Что мне даёт эта связь, а что — забирает?',
    ],
  },
  {
    label: 'Работа и учёба',
    to: '/rasklady/rasklad-na-uchebu',
    linkLabel: 'Расклад на учёбу',
    examples: [
      'Сдам ли я экзамен по математике?',
      'Стоит ли увольняться сейчас?',
      'Как пройдёт собеседование в пятницу?',
      'На что обратить внимание перед защитой?',
    ],
  },
  {
    label: 'Выбор и решения',
    to: '/rasklady/a-ili-b',
    linkLabel: 'Расклад «А или Б»',
    examples: [
      'Дать ему второй шанс или отпустить?',
      'Соглашаться на переезд?',
      'Что поможет мне решиться?',
    ],
  },
  {
    label: 'Путь и самопознание',
    to: '/rasklady/rasklad-na-samopoznanie',
    linkLabel: 'Расклад на самопознание',
    examples: [
      'Что во мне сейчас просит внимания?',
      'Какой мой следующий честный шаг?',
      'Чего я на самом деле хочу в этой ситуации?',
    ],
  },
];

const reframes = [
  { bad: 'Он меня любит?', good: 'Что мне важно понять про наши чувства сейчас?' },
  { bad: 'Когда я разбогатею?', good: 'Что помогает и что мешает моим деньгам прямо сейчас?' },
  { bad: 'Он вернётся?', good: 'Что поможет мне прожить это расставание и что ждёт впереди?' },
  { bad: 'Меня возьмут на эту работу?', good: 'Как мне лучше подготовиться и на что сделать ставку?' },
  { bad: 'Всё будет хорошо?', good: 'Что сейчас в моих силах, чтобы стало лучше?' },
];

// Seeded tilts for the question slips (index-based — identical on SSR + client).
const SLIP_TILTS = [-0.6, 0.4, -0.3, 0.7, -0.5];

const faq = [
  {
    question: 'Как правильно задавать вопросы таро?',
    answer:
      'Формулируй открытый вопрос про себя и своё действие, по одной теме за раз, в настоящем времени. Чем яснее вопрос, тем яснее ответ — карта отвечает ровно на то, что ты на самом деле спросила.',
  },
  {
    question: 'Можно ли задать один вопрос дважды?',
    answer:
      'Конечно. Ситуация меняется — и карта может показать новое. Но если ты спрашиваешь одно и то же в пятый или двадцатый раз, дело обычно не в картах, а в тревоге, и это по-человечески понятно. Карты — зеркало, а не приговор: они не пообещают то, чего не знают. Иногда стоит просто выдохнуть.',
  },
  {
    question: 'Как спросить про конкретного человека?',
    answer:
      'Называй его по имени — так даже точнее. Только помни: карты покажут ситуацию с твоей стороны, а не его тайные мысли. Поэтому «Что мне важно понять про нас с Никитой?» даст больше, чем «О чём он думает?».',
  },
  {
    question: 'Обязательно ли формулировать как «да / нет»?',
    answer:
      'Нет. «Да / нет» хорош, когда нужен быстрый толчок на распутье. «Что» и «как» — когда хочешь разобраться глубже. Оба варианта рабочие, выбирай под то, что тебе сейчас нужнее.',
  },
  {
    question: 'Что делать, если не знаю, что спросить?',
    answer:
      'Начни с того, что не даёт уснуть. Опиши чувство своими словами — «устала гадать, напишет он или нет», — и Таро Кот поможет превратить это в вопрос. А если хочется без давления — просто вытяни карту дня и посмотри, о чём она.',
  },
];

const related = [
  { to: '/rasklady/zadaj-vopros', label: 'Таро на вопрос', note: 'Задай свой вопрос и получи ответ одной картой' },
  { to: '/rasklady/da-ili-net', label: 'Да или Нет', note: 'Когда нужен короткий и чёткий ответ' },
  { to: '/rasklady', label: 'Все расклады', note: '16 раскладов на любую тему и вопрос' },
];

useHead({
  title: TITLE,
  meta: [
    { name: 'description', content: DESCRIPTION },
    {
      name: 'keywords',
      content:
        'как задавать вопросы таро, какие вопросы задать картам таро, как правильно задавать вопросы таро, как задать вопрос таро, вопросы для таро',
    },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: 'https://tarotmeow.ru/og-image.jpg' },
    { property: 'og:locale', content: 'ru_RU' },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: 'Таро Мяу' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { name: 'twitter:image', content: 'https://tarotmeow.ru/og-image.jpg' },
  ],
  link: [
    { rel: 'canonical', href: URL },
    { rel: 'alternate', hreflang: 'ru', href: URL },
    { rel: 'alternate', hreflang: 'x-default', href: URL },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Главная', item: 'https://tarotmeow.ru/' },
          { '@type': 'ListItem', position: 2, name: 'Как задавать вопросы Таро', item: URL },
        ],
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      }),
    },
  ],
});
</script>

<template>
  <div class="py-16 md:py-24">
    <div class="site-container">
      <SeoCrumbs :items="[{ label: 'Главная', to: '/' }, { label: 'Как задавать вопросы Таро' }]" />

      <!-- Hero -->
      <header class="mb-14 text-center md:mb-20">
        <div class="type-overline mb-3.5 text-primary-ink">Как задавать вопросы</div>
        <h1 class="type-display mx-auto max-w-[900px]">Как задавать вопросы таро, чтобы получать ясные ответы</h1>
        <p class="type-body mx-auto mt-4 max-w-2xl text-muted-foreground md:text-[17px]">
          Хороший вопрос — уже половина ответа. Разберёмся, как сформулировать то, что крутится в
          голове ночью, чтобы карты ответили по делу — тепло и честно, без тумана. С живыми
          примерами про него, работу и решения, которые не дают покоя.
        </p>
        <div class="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            as-child
            size="lg"
          >
            <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
          </Button>
          <Button
            as-child
            variant="outline"
            size="lg"
          >
            <a
              :href="TELEGRAM_BOT_URL"
              target="_blank"
              rel="noopener"
            >{{ TELEGRAM_CTA_LABEL }}</a>
          </Button>
        </div>
      </header>

      <!-- Принципы: the article's core, on a taped letter at a reading measure -->
      <ScrollReveal class="mx-auto mb-16 max-w-[760px] md:mb-24">
        <PaperLetter class="md:px-12 md:pb-12 md:pt-14">
          <div class="type-overline mb-3 text-primary-ink">Основа</div>
          <h2 class="type-title">Как звучит сильный вопрос</h2>
          <div class="seo-lines mt-6">
            <div
              v-for="p in principles"
              :key="p.title"
              class="py-5"
            >
              <h3 class="type-subheading text-foreground">{{ p.title }}</h3>
              <p class="letter-text type-body mt-1.5 text-muted-foreground">{{ p.text }}</p>
            </div>
          </div>
        </PaperLetter>
      </ScrollReveal>

      <!-- Примеры по темам: questions written on cream slips -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Какие вопросы задать картам</div>
          <h2 class="type-title">Примеры по темам</h2>
        </ScrollReveal>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ScrollReveal
            v-for="(cat, i) in categories"
            :key="cat.label"
            :delay="((i % 2) + 1) * 80"
          >
            <div class="paper-sheet flex h-full flex-col p-5 md:p-7">
              <h3 class="type-heading text-foreground">{{ cat.label }}</h3>
              <ul class="mt-4 space-y-2.5">
                <li
                  v-for="(ex, j) in cat.examples"
                  :key="ex"
                  class="seo-slip paper-tag type-body px-4 py-2.5 text-paper-stock-ink"
                  :style="{ '--tilt': `${SLIP_TILTS[(i + j) % SLIP_TILTS.length]}deg` }"
                >
                  «{{ ex }}»
                </li>
              </ul>
              <RouterLink
                :to="cat.to"
                class="seo-more type-label mt-5 inline-flex w-fit items-center gap-1 font-semibold"
              >
                {{ cat.linkLabel }} →
              </RouterLink>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <!-- Переформулируй закрытый вопрос -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Работа над ошибками</div>
          <h2 class="type-title">Переформулируй закрытый вопрос</h2>
        </ScrollReveal>
        <div class="mx-auto max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(r, i) in reframes"
            :key="r.bad"
            :delay="((i % 3) + 1) * 80"
          >
            <div class="paper-sheet flex flex-col gap-3 p-5 sm:flex-row sm:items-center md:p-6">
              <p class="type-body flex-1 text-muted-foreground line-through decoration-destructive/60">
                «{{ r.bad }}»
              </p>
              <span class="shrink-0 self-center text-xl leading-none text-primary-ink">→</span>
              <p class="type-body-strong flex-1 text-foreground">«{{ r.good }}»</p>
            </div>
          </ScrollReveal>
        </div>
        <ScrollReveal class="mx-auto mt-10 max-w-[820px]">
          <PaperLetter>
            <p class="letter-text type-body text-muted-foreground md:px-3">
              И глубже механики: спрашивай о том, на что влияешь ты, а не о чужих тайных мыслях — карты
              видят твою сторону ситуации. Не перетягивай один вопрос по двадцать раз: если он не
              отпускает, это чаще про тревогу, чем про карты. А про здоровье лучше спросить врача —
              никакой расклад не заменит осмотр. Карты — зеркало, а не приговор: решение всегда остаётся
              за тобой.
            </p>
          </PaperLetter>
        </ScrollReveal>
      </section>

      <!-- FAQ -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Частые вопросы</div>
          <h2 class="type-title">Коротко о главном</h2>
        </ScrollReveal>
        <div class="mx-auto max-w-[820px] space-y-3.5">
          <ScrollReveal
            v-for="(item, i) in faq"
            :key="i"
            :delay="((i % 3) + 1) * 80"
          >
            <SeoFaqItem
              :question="item.question"
              :answer="item.answer"
            />
          </ScrollReveal>
        </div>
      </section>

      <!-- Related -->
      <section class="mb-16 md:mb-24">
        <ScrollReveal class="mb-8 text-center md:mb-10">
          <div class="type-overline mb-3 text-primary-ink">Куда дальше</div>
          <h2 class="type-title">Задай свой вопрос картам</h2>
        </ScrollReveal>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ScrollReveal
            v-for="(link, i) in related"
            :key="link.to"
            :delay="(i + 1) * 80"
          >
            <RouterLink
              :to="link.to"
              class="ar-lift seo-press paper-sheet group flex h-full flex-col p-5 md:p-6"
            >
              <h3 class="type-subheading text-foreground transition-colors group-hover:text-primary-ink">{{ link.label }}</h3>
              <p class="type-caption mt-2">{{ link.note }}</p>
            </RouterLink>
          </ScrollReveal>
        </div>
      </section>

      <!-- Bottom CTA -->
      <ScrollReveal>
        <SeoFinalCta cat>
          <h2 class="type-title">Готова спросить?</h2>
          <p class="type-body mx-auto mt-3.5 max-w-xl text-muted-foreground md:text-[17px]">
            Сформулируй вопрос по этим принципам — и открой расклад «Таро на вопрос»: одна карта,
            честный персональный ответ. Бесплатно и без регистрации.
          </p>
          <div class="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Button
              as-child
              size="lg"
            >
              <RouterLink to="/rasklady/zadaj-vopros">Расклад «Таро на вопрос»</RouterLink>
            </Button>
            <Button
              as-child
              variant="outline"
              size="lg"
            >
              <a
                :href="TELEGRAM_BOT_URL"
                target="_blank"
                rel="noopener"
              >{{ TELEGRAM_CTA_LABEL }}</a>
            </Button>
          </div>
        </SeoFinalCta>
      </ScrollReveal>
    </div>
  </div>
</template>

<style scoped src="../components/seo/seo-paper.css"></style>

<style scoped>
@media (hover: hover) {
  .ar-lift:hover {
    translate: 0 -2px;
  }
}
</style>
